import React from 'react';
import { User, Leaf, Bell, Menu, LogOut } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

interface HeaderProps {
  onMenuClick: () => void;
  onProfileClick: () => void;
}

export default function Header({ onMenuClick, onProfileClick }: HeaderProps) {
  const { profile, signOut } = useAuth();

  if (!profile) return null;

  return (
    <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo and Brand */}
          <div className="flex items-center space-x-4">
            <button
              onClick={onMenuClick}
              className="lg:hidden p-2 rounded-md text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-colors"
            >
              <Menu className="h-5 w-5" />
            </button>
            <div className="flex items-center space-x-2">
              <div className="bg-emerald-500 p-2 rounded-lg">
                <Leaf className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Yashnar</h1>
                <p className="text-xs text-gray-500 hidden sm:block">Gamify Your Impact</p>
              </div>
            </div>
          </div>

          {/* User Info and Actions */}
          <div className="flex items-center space-x-4">
            {/* Points Display */}
            <div className="hidden sm:flex items-center space-x-2 bg-purple-50 px-3 py-1.5 rounded-full">
              <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
              <span className="text-sm font-medium text-purple-700">
                {profile.points?.toLocaleString() || 0} pts
              </span>
            </div>

            {/* EcoCoins */}
            <div className="hidden sm:flex items-center space-x-2 bg-emerald-50 px-3 py-1.5 rounded-full">
              <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
              <span className="text-sm font-medium text-emerald-700">
                {profile.eco_coins.toLocaleString()} EcoCoins
              </span>
            </div>

            {/* Mobile Combined Display */}
            <div className="sm:hidden flex items-center space-x-3">
              <div className="flex items-center space-x-1 bg-purple-50 px-2 py-1 rounded-full">
                <div className="w-1.5 h-1.5 bg-purple-500 rounded-full"></div>
                <span className="text-xs font-medium text-purple-700">
                  {profile.points?.toLocaleString() || 0}
                </span>
              </div>
              <div className="flex items-center space-x-1 bg-emerald-50 px-2 py-1 rounded-full">
                <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full"></div>
                <span className="text-xs font-medium text-emerald-700">
                  {profile.eco_coins.toLocaleString()}
                </span>
              </div>
            </div>

            {/* Notifications */}
            <button className="relative p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
              <Bell className="h-5 w-5" />
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                3
              </span>
            </button>

            {/* User Profile */}
            <div className="flex items-center space-x-3">
              <div className="hidden sm:block text-right">
                <p className="text-sm font-medium text-gray-900">{profile.username}</p>
                <p className="text-xs text-gray-500">{profile.level}</p>
              </div>
              <button
                onClick={onProfileClick}
                className="focus:outline-none focus:ring-2 focus:ring-emerald-500 rounded-full"
              >
                <img
                  src={profile.avatar_url}
                  alt={profile.username}
                  className="w-10 h-10 rounded-full object-cover border-2 border-emerald-200 hover:border-emerald-300 transition-colors"
                />
              </button>
              
              {/* Sign Out Button */}
              <button
                onClick={signOut}
                className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                title="Sign Out"
              >
                <LogOut className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}